import { Divider } from 'antd';
import React, { FC, memo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getSelectedWarehouse } from 'widgets/IssueInvoiceModal/model/selectors/getSelectedWarehouse';
import { getInvoiceData } from 'widgets/IssueInvoiceModal/model/selectors/getInvoiceData';
import { getSelectedUser } from 'widgets/IssueInvoiceModal/model/selectors/getSelectedUser';
import { getUserName } from 'entities/User';
import { Button } from 'shared/ui/Button';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { getInvoiceItems } from 'widgets/IssueInvoiceModal/model/selectors/getInvoiceItems';
import { useGetCurrencySymbol } from 'app/appState';
import { IssuePersonalInvoice } from 'features/IssuePersonalInvoice';

interface PreviewStepProps {
  onClose: () => void;
}

export const PreviewStep: FC<PreviewStepProps> = memo((props) => {
  const { onClose } = props;

  const { t } = useAppTranslation(['contracts', 'booking']);

  const currencySymbol = useGetCurrencySymbol();
  const selectedWarehouse = useAppSelector(getSelectedWarehouse);
  const selectedUser = useAppSelector(getSelectedUser);
  const invoiceData = useAppSelector(getInvoiceData);
  const invoiceItems = useAppSelector(getInvoiceItems);

  return (
    <div className="px-6">
      <div className="font-semibold text-2xl mb-4">{t('Preview')}</div>

      <div className="border border-border rounded-md p-4 flex gap-10 mb-4">
        <div className="flex flex-1 flex-col">
          <div className="flex flex-col gap-2">
            <div className="font-semibold text-accent">{t('Personal Invoice')}</div>
            <div>
              <div className="flex gap-2 items-center">
                <span className="font-normal">{t('Due date')}:</span>
                <span className="text-accent font-semibold">{getClientDateFormat(invoiceData?.dueDate)}</span>
              </div>
              {invoiceData?.description && (
                <div className="flex gap-2 items-center">
                  <span className="font-normal">{t('Description')}:</span>
                  <span className="text-accent font-semibold">{invoiceData.description}</span>
                </div>
              )}
            </div>

            <Divider className="my-3" />
          </div>
          <div className="flex flex-col gap-2">
            <div className="font-semibold text-accent">{t('Customer')}</div>
            <div>
              <div className="font-normal">{getUserName(selectedUser)}</div>
              <div className="font-normal">{invoiceData?.invoiceEmail}</div>
              <div className="font-normal">{invoiceData?.invoiceAddress}</div>
            </div>

            <Divider className="my-3" />
          </div>
          <div className="flex flex-col gap-2">
            <div className="font-semibold text-accent">{t('Warehouse')}</div>
            <div className="font-normal">{selectedWarehouse?.name}</div>
          </div>
        </div>
        <div className="flex flex-1 flex-col">
          <div className="flex flex-col gap-2">
            {invoiceItems?.map((item, index) => (
              <div className="flex flex-col gap-2" key={`${item?.invoiceItemType}-${index}-${item?.description}`}>
                <div className="font-semibold text-accent">{t(item?.invoiceItemType || '')}</div>
                <div className="flex gap-2 items-center">
                  <span className="font-normal">{t('Initial Price')}:</span>
                  <span className="text-accent font-semibold">
                    {item?.initialPrice} {currencySymbol} × {item?.quantity}
                  </span>
                </div>
                <div className="flex gap-2 items-center">
                  <span className="font-normal">{t('Discount Percent')}:</span>
                  <span className="text-accent font-semibold">{item?.discountPercent || 0}%</span>
                </div>
                <div className="flex gap-2 items-center">
                  <span className="font-normal">{t('VAT')}:</span>
                  <span className="text-accent font-semibold">{item?.vatRatePercent || 0}%</span>
                </div>
                {item?.description && (
                  <div className="flex gap-2 items-center">
                    <span className="font-normal">{t('Description')}:</span>
                    <span className="text-accent font-semibold">{item.description}</span>
                  </div>
                )}
                <div className="flex gap-2 items-center">
                  <span className="font-normal">{t('Total Price')}:</span>
                  <span className="text-accent font-semibold">
                    {item?.totalPrice} {currencySymbol}
                  </span>
                </div>

                <Divider className="my-3" />
              </div>
            ))}
            <div className="flex flex-col gap-2 self-end items-end">
              <div className="font-semibold text-accent text-lg">{t('Total Invoice Amount')}</div>
              <div className="font-semibold text-accent text-lg ">
                {invoiceData?.amount} {currencySymbol}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <IssuePersonalInvoice
          invoiceData={invoiceData}
          invoiceItems={invoiceItems}
          userId={selectedUser?.userId}
          action={<Button>{t('Create Invoice')}</Button>}
          warehouseId={selectedWarehouse?.warehouseId}
          onSuccess={onClose}
        />
      </div>
    </div>
  );
});
